import { api } from '../lib/api-client.js';

import type {
  CreateInspectionTemplateInput,
  InspectionAppliesTo,
  InspectionSchema,
  InspectionTemplateFiltersQuery,
  InspectionTemplateStatus,
  UpdateInspectionTemplateInput,
  UpdateInspectionVersionInput,
} from '@sitewise/shared';

/* ----------------------------- Types --------------------------------- */

export interface InspectionTemplateSummary {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  appliesTo: InspectionAppliesTo;
  status: InspectionTemplateStatus;
  isArchived: boolean;
  presetSlug: string | null;
  currentVersion: number | null;
  publishedVersion: number | null;
  metadata: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionTemplateVersionSummary {
  id: string;
  templateId: string;
  version: number;
  schema: InspectionSchema;
  publishedAt: string | null;
  publishedBy: string | null;
  createdAt: string;
}

/** Response from `GET /:id` and `POST /` (create). */
export interface InspectionTemplateDetail {
  template: InspectionTemplateSummary;
  versions: InspectionTemplateVersionSummary[];
  currentVersion: InspectionTemplateVersionSummary | null;
}

export interface SeedPresetsResult {
  inserted: number;
  skipped: number;
  insertedSlugs: string[];
}

/* ----------------------------- Helpers ------------------------------- */

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) search.set(k, v.join(','));
    } else {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/* ----------------------------- Calls --------------------------------- */

export async function listInspectionTemplates(
  filters: Partial<InspectionTemplateFiltersQuery> = {},
) {
  return api<{ templates: InspectionTemplateSummary[] }>(
    `/inspection-templates${qs(filters as Record<string, unknown>)}`,
  );
}

export async function fetchInspectionTemplate(id: string) {
  return api<InspectionTemplateDetail>(`/inspection-templates/${id}`);
}

export async function createInspectionTemplate(input: CreateInspectionTemplateInput) {
  return api<InspectionTemplateDetail>('/inspection-templates', {
    method: 'POST',
    body: input,
  });
}

export async function updateInspectionTemplate(
  id: string,
  input: UpdateInspectionTemplateInput,
) {
  return api<{ template: InspectionTemplateSummary }>(`/inspection-templates/${id}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function archiveInspectionTemplate(id: string) {
  return api<{ template: InspectionTemplateSummary }>(`/inspection-templates/${id}/archive`, {
    method: 'POST',
  });
}

export async function createDraftVersion(templateId: string) {
  return api<{ version: InspectionTemplateVersionSummary }>(
    `/inspection-templates/${templateId}/versions`,
    { method: 'POST' },
  );
}

export async function updateDraftVersion(
  templateId: string,
  versionId: string,
  input: UpdateInspectionVersionInput,
) {
  return api<{ version: InspectionTemplateVersionSummary }>(
    `/inspection-templates/${templateId}/versions/${versionId}`,
    { method: 'PATCH', body: input },
  );
}

export async function publishVersion(templateId: string, versionId: string) {
  return api<{
    template: InspectionTemplateSummary;
    version: InspectionTemplateVersionSummary;
  }>(`/inspection-templates/${templateId}/versions/${versionId}/publish`, {
    method: 'POST',
  });
}

export async function seedInspectionPresets() {
  return api<SeedPresetsResult>('/inspection-templates/seed-presets', {
    method: 'POST',
  });
}
